import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          // Let cart-items-component consume the same CartLinesUpdateEvent and morph
          // the drawer content before we show it. This is especially important for
          // the empty-cart -> first-item transition.
          await new Promise((resolve) => requestAnimationFrame(resolve));
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

class CartAddonToggles extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.shippingVariantId = this.dataset.shippingVariantId;
    this.priorityVariantId = this.dataset.priorityVariantId;
    this.querySelectorAll('[data-cart-addon]').forEach((input) => {
      input.addEventListener('change', (event) => this.#onToggle(event));
    });
    queueMicrotask(() => this.#ensureDefaultAddons());
  }

  #storageKey(type) {
    return `bellaro-cart-addon-disabled-${type}`;
  }

  async #ensureDefaultAddons() {
    if (window.BellaroCartAddonsUpdating) return;
    const shipping = this.querySelector('[data-cart-addon="shipping"]');
    const priority = this.querySelector('[data-cart-addon="priority"]');
    const regularQuantity = Number(this.dataset.regularCartQuantity);

    if (regularQuantity === 0) {
      sessionStorage.removeItem(this.#storageKey('shipping'));
      sessionStorage.removeItem(this.#storageKey('priority'));
      const updates = {};
      if (this.dataset.shippingInCart === 'true') updates[this.shippingVariantId] = 0;
      if (this.dataset.priorityInCart === 'true') updates[this.priorityVariantId] = 0;
      if (Object.keys(updates).length) await this.#cartRequest(Theme.routes.cart_update_url, { updates }, 'remove');
      return;
    }

    const missing = [];
    if (shipping && this.dataset.shippingInCart !== 'true' && sessionStorage.getItem(this.#storageKey('shipping')) !== 'true') {
      shipping.checked = true;
      missing.push({ id: Number(this.shippingVariantId), quantity: 1 });
    }
    if (priority && this.dataset.priorityInCart !== 'true' && sessionStorage.getItem(this.#storageKey('priority')) !== 'true') {
      priority.checked = true;
      missing.push({ id: Number(this.priorityVariantId), quantity: 1 });
    }
    if (missing.length) await this.#cartRequest(Theme.routes.cart_add_url, { items: missing }, 'add');
  }

  async #onToggle(event) {
    const input = event.currentTarget;
    const type = input.dataset.cartAddon;
    const variantId = type === 'shipping' ? this.shippingVariantId : this.priorityVariantId;
    if (input.checked) sessionStorage.removeItem(this.#storageKey(type));
    else sessionStorage.setItem(this.#storageKey(type), 'true');

    try {
      if (input.checked) {
        await this.#cartRequest(Theme.routes.cart_add_url, { items: [{ id: Number(variantId), quantity: 1 }] }, 'add');
      } else {
        await this.#cartRequest(Theme.routes.cart_update_url, { updates: { [variantId]: 0 } }, 'remove');
      }
    } catch (error) {
      input.checked = !input.checked;
      if (input.checked) sessionStorage.removeItem(this.#storageKey(type));
      else sessionStorage.setItem(this.#storageKey(type), 'true');
      console.error('[cart-addons] Update failed', error);
    }
  }

  async #cartRequest(url, payload, action) {
    if (window.BellaroCartAddonsUpdating) return;
    window.BellaroCartAddonsUpdating = true;
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    if (!sectionIds.includes('cart-drawer-section')) sectionIds.push('cart-drawer-section');
    const promise = CartLinesUpdateEvent.createPromise();
    const items = payload.items || [];
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: promise.promise,
      })
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...payload,
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Unable to update cart add-ons.');
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: state.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-addon-toggles',
          didError: false,
        },
      });
      return state;
    } catch (error) {
      promise.reject(error);
      throw error;
    } finally {
      window.BellaroCartAddonsUpdating = false;
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!customElements.get('cart-addon-toggles')) {
  customElements.define('cart-addon-toggles', CartAddonToggles);
}
